/**
 * Normalize Firestore, poll, upload, and viewer runtime into one observation object.
 */

import { CAPTURE_PROFILE, normalizeCaptureProfile, normalizeSourceFormat } from '../../../constants/captureSchema';
import {
  PIPELINE_STAGE,
  normalizePipelineStage,
  pipelineStageToSegmentIndex,
  progressMessageForStage,
  isTerminalPipelineStage,
} from '../../../constants/pipelineStages';
import { readCaptureFrameFields } from '../../../utils/pipeline/pipelineFrameFields';
import {
  captureHasReconstructionArtifact,
  isCaptureMessagingComplete,
  isCaptureViewerAssetReady,
} from '../../../utils/viewerReadiness';

const ACTIVE_RUN_STATUSES = new Set(['processing', 'queued']);

function parseTimestampMs(value) {
  if (value == null) return null;
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  if (typeof value?.toMillis === 'function') return value.toMillis();
  if (typeof value?.seconds === 'number') return value.seconds * 1000;
  const parsed = Date.parse(String(value));
  return Number.isFinite(parsed) ? parsed : null;
}

function stageRank(stage) {
  const idx = pipelineStageToSegmentIndex(stage);
  return idx >= 0 ? idx : -1;
}

function resolveCanonicalStageFromObservation(proc, progress, poll = null) {
  const candidates = [
    normalizePipelineStage(proc?.stage),
    normalizePipelineStage(poll?.pipelineStage),
    normalizePipelineStage(poll?.stageFloor),
  ].filter(Boolean);

  const msg = String(progress?.message || '').trim().toUpperCase();
  if (msg === 'STITCHING 360 VIDEO') candidates.push(PIPELINE_STAGE.STITCHING_360);
  if (msg === 'SOG CONVERSION' || msg === 'PREPARING VIEWER') candidates.push(PIPELINE_STAGE.RENDERING);
  if (msg === 'VIDEO UPLOAD' || msg === 'UPLOADING VIDEO') candidates.push(PIPELINE_STAGE.UPLOADING);
  if (msg === 'IMAGE EXTRACTION') candidates.push(PIPELINE_STAGE.ANALYZING_FRAMES);
  if (msg === 'ANALYZING IMAGES') candidates.push(PIPELINE_STAGE.BUILDING_STRUCTURE);
  if (msg === 'CREATING 3D MODEL') candidates.push(PIPELINE_STAGE.RENDERING);

  const status = String(poll?.status || proc?.status || '').toLowerCase();
  const liveCandidates = candidates.filter((stage) => (
    !isTerminalPipelineStage(stage) || status === 'failed' || status === 'cancelled'
  ));
  if (liveCandidates.length === 0) return null;

  return liveCandidates.reduce((best, stage) => (
    stageRank(stage) > stageRank(best) ? stage : best
  ));
}

function isViewerAssetEncoding(storage, isViewerAssetReady) {
  if (isViewerAssetReady) return false;
  const vs = String(storage?.viewerStatus || '').toLowerCase();
  return vs === 'pending' || vs === 'processing' || vs === 'client-pending';
}

const WORKER_HEARTBEAT_RECENCY_MS = 5 * 60 * 1000;

function isRecentWorkerHeartbeat(workerHeartbeatAt) {
  const ms = parseTimestampMs(workerHeartbeatAt);
  if (ms == null) return false;
  return Date.now() - ms < WORKER_HEARTBEAT_RECENCY_MS;
}

function hasWorkerFoundEvidence({
  processingStatus,
  canonicalStage,
  runpodJobStatus,
  framesExtracted,
  infraRetryCount,
  workerHeartbeatAt,
}) {
  const proc = String(processingStatus || '').toLowerCase();
  const rp = String(runpodJobStatus || '').toLowerCase();
  if (isRecentWorkerHeartbeat(workerHeartbeatAt)) return true;
  if (rp === 'in_progress' || rp === 'running') return true;
  if (canonicalStage && canonicalStage !== PIPELINE_STAGE.UPLOADING && !isTerminalPipelineStage(canonicalStage)) return true;
  if (Number.isFinite(framesExtracted) && framesExtracted > 0 && proc !== 'queued') return true;
  if (Number(infraRetryCount) >= 1 && proc === 'processing') return true;
  return false;
}

function hasServerPipelineProgressEvidence(proc, frameFields, canonicalStage) {
  if (Number.isFinite(frameFields.framesExtracted) && frameFields.framesExtracted > 0) {
    return true;
  }
  if (
    canonicalStage
    && canonicalStage !== PIPELINE_STAGE.UPLOADING
    && !isTerminalPipelineStage(canonicalStage)
  ) {
    return true;
  }
  const stage = String(proc?.stage || '').trim().toLowerCase();
  if (
    stage
    && !isTerminalPipelineStage(stage)
    && !['upload', 'uploadpending', 'uploading', 'queued', ''].includes(stage)
  ) {
    return true;
  }
  return false;
}

export function normalizeCaptureObservation({
  capture = {},
  poll = null,
  uploadCtx = {},
  viewerRuntime = {},
  env = {},
} = {}) {
  const proc = capture?.processing || {};
  const progress = capture?.progress || {};
  const storage = capture?.storage || {};
  const processingStatus = String(
    poll?.status || proc.status || ''
  ).toLowerCase();
  const progressMessage = progress?.message ?? poll?.message ?? null;
  const isViewerAssetReady = isCaptureViewerAssetReady(capture);
  const canonicalStage = resolveCanonicalStageFromObservation(proc, {
    ...progress,
    message: progressMessage,
  }, poll);
  const frameFields = readCaptureFrameFields(proc);
  const runpodJobStatus = poll?.runpodJobStatus ?? null;
  const runEpoch = poll?.runEpoch
    ?? progress?.runpodCallbackEpoch
    ?? null;
  const captureKey = capture?.id ?? capture?.captureId ?? null;
  const isClientUploading = !!(
    uploadCtx?.activeCaptureId != null
    && captureKey != null
    && uploadCtx.activeCaptureId === captureKey
    && uploadCtx?.uploadPhase === 'uploading'
    && !hasServerPipelineProgressEvidence(proc, frameFields, canonicalStage)
  );
  const workerActive = hasWorkerFoundEvidence({
    processingStatus: proc.status,
    canonicalStage,
    runpodJobStatus,
    framesExtracted: frameFields.framesExtracted,
    infraRetryCount: proc.infraRetryCount,
    workerHeartbeatAt: progress?.workerHeartbeatAt,
  });
  const isQueued = (
    !isClientUploading
    && !workerActive
    && (
      processingStatus === 'queued'
      || String(runpodJobStatus || '').toLowerCase() === 'in_queue'
      || String(progressMessage || '').toLowerCase() === 'queueing'
    )
  );
  const viewerPrepStartedAtMs = parseTimestampMs(storage.viewerPrepStartedAt)
    ?? parseTimestampMs(progress?.updatedAt)
    ?? parseTimestampMs(capture?.updatedAt);
  const viewerPrepMs = viewerPrepStartedAtMs != null
    ? Math.max(0, Date.now() - viewerPrepStartedAtMs)
    : null;
  const captureProfile = normalizeCaptureProfile(proc.captureProfile);
  const sourceFormat = normalizeSourceFormat(proc.sourceFormat);
  const is360Upload = captureProfile === CAPTURE_PROFILE.THREE_SIXTY
    || sourceFormat === 'insv';

  return {
    capture,
    proc,
    progress,
    storage,
    processingStatus,
    progressMessage,
    canonicalStage,
    frameFields,
    runpodJobStatus,
    runEpoch,
    poll,
    uploadCtx,
    viewerRuntime,
    env,
    isClientUploading,
    workerActive,
    isQueued,
    viewerPrepStartedAtMs,
    viewerPrepMs,
    captureProfile,
    sourceFormat,
    is360Upload,
    isActiveRun: ACTIVE_RUN_STATUSES.has(processingStatus),
    isMessagingComplete: isCaptureMessagingComplete(capture, {
      isActiveReupload: isClientUploading,
    }),
    hasReconstructionArtifact: captureHasReconstructionArtifact(capture),
    isViewerAssetReady,
    isViewerAssetEncoding: isViewerAssetEncoding(storage, isViewerAssetReady),
    stageMessage: progressMessageForStage(canonicalStage),
    isPollChecking: poll?.status === 'checking' || processingStatus === 'checking',
  };
}
